// Forgot Password Page — 3-step flow: Email → OTP → Reset Password

import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { FiArrowLeft, FiMail, FiLock, FiEye, FiEyeOff, FiCheck } from 'react-icons/fi';
import { capcrmlogo } from '../assets';
import authApi from '../api/authApi';

const ForgotPasswordPage = () => {
  const navigate = useNavigate();

  const [step, setStep] = useState(1);
  const [email, setEmail] = useState('');
  const [otp, setOtp] = useState(['', '', '', '', '', '']);
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showNew, setShowNew] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [resendMsg, setResendMsg] = useState('');
  const [success, setSuccess] = useState(false);

  // ── Step 1: Send OTP ──
  const handleSendOTP = async (e) => {
    e.preventDefault();
    if (!email.trim()) { setError('Email is required'); return; }
    setLoading(true);
    setError('');
    try {
      await authApi.forgotPassword(email);
      setStep(2);
    } catch (err) {
      setError(err?.error || err?.message || 'Failed to send OTP. Check email configuration.');
    } finally {
      setLoading(false);
    }
  };

  // ── Step 2: OTP boxes ──
  const handleOtpChange = (index, value) => {
    if (!/^\d?$/.test(value)) return;
    const next = [...otp];
    next[index] = value;
    setOtp(next);
    setError('');
    if (value && index < 5) {
      document.getElementById(`otp-${index + 1}`)?.focus();
    }
  };

  const handleOtpKeyDown = (index, e) => {
    if (e.key === 'Backspace' && !otp[index] && index > 0) {
      document.getElementById(`otp-${index - 1}`)?.focus();
    }
  };

  const handleOtpPaste = (e) => {
    e.preventDefault();
    const pasted = e.clipboardData.getData('text').replace(/\D/g, '').slice(0, 6);
    const next = [...otp];
    pasted.split('').forEach((d, i) => { next[i] = d; });
    setOtp(next);
    document.getElementById(`otp-${Math.min(pasted.length, 5)}`)?.focus();
  };

  const handleVerifyOTP = (e) => {
    e.preventDefault();
    const otpStr = otp.join('');
    if (otpStr.length < 6) { setError('Please enter all 6 digits'); return; }
    setError('');
    setStep(3);
  };

  const handleResendOTP = async () => {
    setLoading(true);
    setError('');
    setResendMsg('');
    try {
      await authApi.forgotPassword(email);
      setResendMsg('OTP resent successfully.');
      setOtp(['', '', '', '', '', '']);
      document.getElementById('otp-0')?.focus();
    } catch (err) {
      setError(err?.error || 'Failed to resend OTP');
    } finally {
      setLoading(false);
    }
  };

  // ── Step 3: Reset Password ──
  const handleResetPassword = async (e) => {
    e.preventDefault();
    if (newPassword.length < 8) { setError('Password must be at least 8 characters'); return; }
    if (newPassword !== confirmPassword) { setError('Passwords do not match'); return; }
    setLoading(true);
    setError('');
    try {
      const otpStr = otp.join('');
      await authApi.resetPassword(email, otpStr, newPassword);
      setSuccess(true);
      setTimeout(() => navigate('/login'), 2500);
    } catch (err) {
      const errMsg = err?.error || '';
      if (errMsg.toLowerCase().includes('request a new')) {
        setStep(1);
        setOtp(['', '', '', '', '', '']);
        setError(errMsg);
      } else if (errMsg.toLowerCase().includes('invalid') || errMsg.toLowerCase().includes('expired')) {
        setStep(2);
        setOtp(['', '', '', '', '', '']);
        setError(errMsg);
      } else {
        setError(errMsg || 'Password reset failed. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  };

  const stepMeta = {
    1: { title: 'Forgot password?', sub: 'Enter your email address and we\'ll send you a one-time code.' },
    2: { title: 'Check your email', sub: `A 6-digit OTP was sent to ${email}. It expires in 10 minutes.` },
    3: { title: 'Set new password', sub: 'Choose a strong password with at least 8 characters.' },
  };

  return (
    <div className="min-h-screen flex">

      {/* Mobile gradient background */}
      <div className="lg:hidden fixed inset-0 bg-gradient-to-br from-violet-700 via-indigo-800 to-slate-900 overflow-hidden pointer-events-none">
        <div className="absolute -top-24 -right-24 w-80 h-80 rounded-full bg-white/5" />
        <div className="absolute top-1/2 -translate-y-1/2 -left-20 w-64 h-64 rounded-full bg-white/5" />
        <div className="absolute -bottom-16 right-0 w-72 h-72 rounded-full bg-violet-500/20" />
        <div className="absolute bottom-1/3 left-1/2 w-40 h-40 rounded-full bg-indigo-400/10" />
      </div>

      {/* Desktop: left branding panel */}
      <div className="hidden lg:flex lg:w-5/12 xl:w-1/2 flex-col justify-between p-12 bg-gradient-to-br from-violet-700 via-indigo-800 to-slate-900 relative overflow-hidden">
        <div className="absolute inset-0 overflow-hidden pointer-events-none">
          <div className="absolute -top-24 -right-24 w-96 h-96 rounded-full bg-white/5" />
          <div className="absolute bottom-0 -left-20 w-80 h-80 rounded-full bg-white/5" />
        </div>

        {/* Logo */}
        <div className="relative flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl flex items-center justify-center">
            <img src={capcrmlogo} alt="CAP" className="w-full h-full object-contain" />
          </div>
          <div>
            <p className="text-white font-bold text-base leading-tight">CAP Admin</p>
            <p className="text-violet-300 text-xs">Coimbatore Auto Parts</p>
          </div>
        </div>

        {/* Middle */}
        <div className="relative space-y-8">
          <div className="w-12 h-1 bg-gradient-to-r from-violet-400 to-transparent rounded-full" />
          <h2 className="text-4xl xl:text-5xl font-black text-white leading-tight">
            Account<br />
            <span className="text-violet-300">recovery</span><br />
            made easy.
          </h2>
          <p className="text-slate-300 text-base max-w-sm">
            Secure your admin account with a quick password reset in just a few steps.
          </p>

          {/* Step progress */}
          <div className="flex gap-4 pt-2">
            {[
              { n: 1, label: 'Email' },
              { n: 2, label: 'Verify' },
              { n: 3, label: 'Reset' },
            ].map(({ n, label }) => (
              <div key={n} className="flex items-center gap-2">
                <div className={`w-7 h-7 rounded-full flex items-center justify-center text-xs font-bold transition-all duration-300 ${
                  n < step ? 'bg-emerald-500 text-white' :
                  n === step ? 'bg-white text-slate-900' :
                  'bg-white/10 text-slate-400'
                }`}>
                  {n < step ? <FiCheck className="w-3.5 h-3.5" /> : n}
                </div>
                <span className={`text-xs font-medium transition-colors duration-300 ${n === step ? 'text-white' : 'text-slate-500'}`}>
                  {label}
                </span>
              </div>
            ))}
          </div>
        </div>

        <p className="text-slate-400 text-xs relative">© {new Date().getFullYear()} Coimbatore Auto Parts. All rights reserved.</p>
      </div>

      {/* Right / Mobile form panel */}
      <div className="flex-1 flex flex-col items-center justify-start sm:justify-center overflow-y-auto py-10 px-5 sm:p-8 lg:p-6 relative z-10 lg:bg-white lg:justify-center">

        {/* Mobile brand header */}
        <div className="lg:hidden text-center mb-7 animate-fade-in-up">
          <div className="inline-flex w-16 h-16 rounded-2xl items-center justify-center mb-4">
            <img src={capcrmlogo} alt="CAP" className="w-full h-full object-contain" />
          </div>
          <h2 className="text-white font-black text-2xl tracking-tight">CAP Admin</h2>
          <p className="text-violet-300 text-sm mt-1">Coimbatore Auto Parts</p>
        </div>

        {/* Form card */}
        <div className="w-full max-w-sm animate-fade-in-up" style={{ animationDelay: '0.08s' }}>
          <div className="bg-white rounded-3xl p-7 sm:p-8 shadow-2xl shadow-black/30 lg:shadow-none lg:rounded-none lg:p-0 lg:bg-transparent">

            {/* Desktop logo */}
            <div className="hidden lg:flex items-center gap-3 mb-10">
              <div className="w-9 h-9 rounded-xl flex items-center justify-center">
                <img src={capcrmlogo} alt="CAP" className="w-full h-full object-contain" />
              </div>
              <div>
                <p className="font-bold text-slate-800">CAP Admin</p>
                <p className="text-slate-400 text-xs">Coimbatore Auto Parts</p>
              </div>
            </div>

            {/* Mobile step bar */}
            <div className="lg:hidden flex gap-1.5 mb-6">
              {[1, 2, 3].map((s) => (
                <div
                  key={s}
                  className={`h-1 rounded-full flex-1 transition-all duration-400 ${s <= step ? 'bg-violet-500' : 'bg-slate-200'}`}
                />
              ))}
            </div>

            {/* Success state */}
            {success ? (
              <div className="py-4 text-center space-y-3">
                <div className="w-16 h-16 rounded-full bg-emerald-100 flex items-center justify-center mx-auto">
                  <FiCheck className="w-8 h-8 text-emerald-600" />
                </div>
                <h1 className="text-xl font-bold text-slate-900">Password reset!</h1>
                <p className="text-slate-500 text-sm">Redirecting you to login...</p>
              </div>
            ) : (
              <>
                <h1 className="text-2xl font-bold text-slate-900 mb-1">{stepMeta[step].title}</h1>
                <p className="text-slate-500 text-sm mb-7">{stepMeta[step].sub}</p>

                {/* Error banner */}
                {error && (
                  <div className="mb-5 px-4 py-3 rounded-xl bg-rose-50 border border-rose-200 text-rose-700 text-sm font-medium">
                    {error}
                  </div>
                )}

                {/* ── STEP 1 ── */}
                {step === 1 && (
                  <form onSubmit={handleSendOTP} className="space-y-5">
                    <div>
                      <label className="block text-sm font-semibold text-slate-700 mb-2">
                        Email address <span className="text-rose-500">*</span>
                      </label>
                      <div className="relative">
                        <FiMail className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-400 w-4 h-4 pointer-events-none" />
                        <input
                          type="email"
                          value={email}
                          onChange={(e) => { setEmail(e.target.value); setError(''); }}
                          placeholder="admin@example.com"
                          disabled={loading}
                          className="w-full pl-11 pr-4 py-3 border border-slate-200 rounded-xl text-slate-800 text-sm placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-violet-300 focus:border-violet-400 transition-all disabled:opacity-60"
                        />
                      </div>
                    </div>

                    <button
                      type="submit"
                      disabled={loading}
                      className="w-full py-3 px-4 rounded-xl bg-violet-600 hover:bg-violet-700 text-white font-semibold text-sm transition-all disabled:opacity-60 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                    >
                      {loading && <span className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />}
                      {loading ? 'Sending...' : 'Send OTP'}
                    </button>

                    <div className="text-center">
                      <Link to="/login" className="text-sm text-violet-600 hover:text-violet-700 font-medium inline-flex items-center gap-1 transition-colors">
                        <FiArrowLeft className="w-3.5 h-3.5" /> Back to Login
                      </Link>
                    </div>
                  </form>
                )}

                {/* ── STEP 2 ── */}
                {step === 2 && (
                  <form onSubmit={handleVerifyOTP} className="space-y-5">
                    {resendMsg && (
                      <div className="px-4 py-3 rounded-xl bg-emerald-50 border border-emerald-200 text-emerald-700 text-sm font-medium flex items-center gap-2">
                        <FiCheck className="w-4 h-4 shrink-0" /> {resendMsg}
                      </div>
                    )}

                    <div>
                      <label className="block text-sm font-semibold text-slate-700 mb-3">
                        Enter 6-digit OTP
                      </label>
                      <div className="flex gap-2 justify-between" onPaste={handleOtpPaste}>
                        {otp.map((digit, i) => (
                          <input
                            key={i}
                            id={`otp-${i}`}
                            type="text"
                            inputMode="numeric"
                            maxLength={1}
                            value={digit}
                            onChange={(e) => handleOtpChange(i, e.target.value)}
                            onKeyDown={(e) => handleOtpKeyDown(i, e)}
                            autoFocus={i === 0}
                            className="w-11 h-12 text-center text-lg font-bold border-2 border-slate-200 rounded-xl text-slate-800 focus:outline-none focus:ring-2 focus:ring-violet-300 focus:border-violet-500 transition-all"
                          />
                        ))}
                      </div>
                    </div>

                    <button
                      type="submit"
                      className="w-full py-3 px-4 rounded-xl bg-violet-600 hover:bg-violet-700 text-white font-semibold text-sm transition-all flex items-center justify-center"
                    >
                      Verify OTP
                    </button>

                    <div className="flex items-center justify-between text-sm">
                      <button
                        type="button"
                        onClick={() => { setStep(1); setError(''); setResendMsg(''); setOtp(['', '', '', '', '', '']); }}
                        className="text-slate-500 hover:text-slate-700 font-medium inline-flex items-center gap-1 transition-colors"
                      >
                        <FiArrowLeft className="w-3.5 h-3.5" /> Back
                      </button>
                      <button
                        type="button"
                        onClick={handleResendOTP}
                        disabled={loading}
                        className="text-violet-600 hover:text-violet-700 font-medium disabled:opacity-60 transition-colors"
                      >
                        {loading ? 'Sending...' : 'Resend OTP'}
                      </button>
                    </div>
                  </form>
                )}

                {/* ── STEP 3 ── */}
                {step === 3 && (
                  <form onSubmit={handleResetPassword} className="space-y-5">
                    <div>
                      <label className="block text-sm font-semibold text-slate-700 mb-2">
                        New password <span className="text-rose-500">*</span>
                      </label>
                      <div className="relative">
                        <FiLock className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-400 w-4 h-4 pointer-events-none" />
                        <input
                          type={showNew ? 'text' : 'password'}
                          value={newPassword}
                          onChange={(e) => { setNewPassword(e.target.value); setError(''); }}
                          placeholder="Min. 8 characters"
                          disabled={loading}
                          className="w-full pl-11 pr-12 py-3 border border-slate-200 rounded-xl text-slate-800 text-sm placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-violet-300 focus:border-violet-400 transition-all disabled:opacity-60"
                        />
                        <button type="button" onClick={() => setShowNew(!showNew)} className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600 transition-colors">
                          {showNew ? <FiEyeOff className="w-5 h-5" /> : <FiEye className="w-5 h-5" />}
                        </button>
                      </div>
                    </div>

                    <div>
                      <label className="block text-sm font-semibold text-slate-700 mb-2">
                        Confirm password <span className="text-rose-500">*</span>
                      </label>
                      <div className="relative">
                        <FiLock className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-400 w-4 h-4 pointer-events-none" />
                        <input
                          type={showConfirm ? 'text' : 'password'}
                          value={confirmPassword}
                          onChange={(e) => { setConfirmPassword(e.target.value); setError(''); }}
                          placeholder="Confirm your password"
                          disabled={loading}
                          className="w-full pl-11 pr-12 py-3 border border-slate-200 rounded-xl text-slate-800 text-sm placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-violet-300 focus:border-violet-400 transition-all disabled:opacity-60"
                        />
                        <button type="button" onClick={() => setShowConfirm(!showConfirm)} className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600 transition-colors">
                          {showConfirm ? <FiEyeOff className="w-5 h-5" /> : <FiEye className="w-5 h-5" />}
                        </button>
                      </div>
                    </div>

                    <button
                      type="submit"
                      disabled={loading}
                      className="w-full py-3 px-4 rounded-xl bg-violet-600 hover:bg-violet-700 text-white font-semibold text-sm transition-all disabled:opacity-60 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                    >
                      {loading && <span className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />}
                      {loading ? 'Resetting...' : 'Reset Password'}
                    </button>

                    <div className="text-center">
                      <button
                        type="button"
                        onClick={() => { setStep(2); setError(''); }}
                        className="text-sm text-slate-500 hover:text-slate-700 font-medium inline-flex items-center gap-1 transition-colors"
                      >
                        <FiArrowLeft className="w-3.5 h-3.5" /> Back
                      </button>
                    </div>
                  </form>
                )}
              </>
            )}

          </div>
        </div>

        <p className="text-xs text-center mt-6 lg:mt-8 text-white/40 lg:text-slate-400 animate-fade-in-up" style={{ animationDelay: '0.16s' }}>
          © {new Date().getFullYear()} Coimbatore Auto Parts · All rights reserved
        </p>
      </div>

    </div>
  );
};

export default ForgotPasswordPage;
